/**
 * Autofill-from-resume-source flow: the pure state machine behind
 * `AutofillSourceDialog`.
 *
 * Autofill is a two-legged operation — persist the chosen resume as a report
 * source, then run the fill — and the second leg cannot start until the parent
 * has the new source in its own state. Modelling that as data (rather than as
 * ad-hoc booleans inside the component) is what makes the "awaiting-source"
 * leg testable without a network or a React tree.
 */

export const RESUME_SOURCE_TYPE = 'RESUME';

/**
 * Exactly the body the resume tab of AddSourceDialog posts today. Declared as
 * a type alias, not an interface, so it stays assignable to the caller's
 * `Record<string, unknown>` source-submit signature.
 */
export type ResumeSourcePayload = {
  type: typeof RESUME_SOURCE_TYPE;
  sourceId: string;
};

export function buildResumeSourcePayload(resumeId: string): ResumeSourcePayload {
  return { type: RESUME_SOURCE_TYPE, sourceId: resumeId };
}

export type AutofillPhase = 'idle' | 'saving' | 'awaiting-source' | 'running';

export interface AutofillFlowState {
  phase: AutofillPhase;
  /** Inline, in-dialog message. Run failures are toasted by the caller instead. */
  error: string | null;
}

export type AutofillFlowEvent =
  | { type: 'confirm'; selectedResumeId?: string }
  | { type: 'save-settled'; ok: boolean }
  | { type: 'sources-changed'; sourceCount: number }
  | { type: 'awaiting-source-timeout' }
  | { type: 'run-settled'; ok: boolean }
  | { type: 'reset' };

export const AUTOFILL_FLOW_MESSAGES = {
  /** Same wording the shared sources dialog uses, so nothing reads as new. */
  noSelection: 'Please select a resume.',
  saveFailed: 'Unable to save source. Please try again.',
  runNotStarted: 'Autofill could not start. Please try again.',
} as const;

export const INITIAL_AUTOFILL_FLOW_STATE: AutofillFlowState = { phase: 'idle', error: null };

const IDLE_WITH = (error: string | null): AutofillFlowState => ({ phase: 'idle', error });

export function autofillFlowReducer(
  state: AutofillFlowState,
  event: AutofillFlowEvent
): AutofillFlowState {
  switch (event.type) {
    case 'confirm': {
      // Anything already in flight owns the flow; a second click is a no-op.
      if (state.phase !== 'idle') return state;
      if (!event.selectedResumeId) return IDLE_WITH(AUTOFILL_FLOW_MESSAGES.noSelection);
      return { phase: 'saving', error: null };
    }
    case 'save-settled': {
      if (state.phase !== 'saving') return state;
      return event.ok
        ? { phase: 'awaiting-source', error: null }
        : IDLE_WITH(AUTOFILL_FLOW_MESSAGES.saveFailed);
    }
    case 'sources-changed': {
      if (state.phase !== 'awaiting-source') return state;
      // The run refuses to start against an empty source list, so hold here
      // until the just-saved source is actually visible to the caller.
      if (event.sourceCount < 1) return state;
      return { phase: 'running', error: null };
    }
    case 'awaiting-source-timeout': {
      if (state.phase !== 'awaiting-source') return state;
      return IDLE_WITH(AUTOFILL_FLOW_MESSAGES.runNotStarted);
    }
    case 'run-settled': {
      if (state.phase !== 'running') return state;
      // A failed run has already surfaced its own toast; a second inline copy
      // of the same message would just be noise.
      return IDLE_WITH(null);
    }
    case 'reset':
      return INITIAL_AUTOFILL_FLOW_STATE;
    default:
      return state;
  }
}

export function isAutofillFlowBusy(state: AutofillFlowState): boolean {
  return state.phase !== 'idle';
}

export interface AutofillConfirmDescriptor {
  label: string;
  busy: boolean;
  disabled: boolean;
}

/**
 * Confirm-button presentation. `isRunningExternally` mirrors the caller's own
 * in-flight flag (e.g. an autofill kicked off from elsewhere) so the button
 * stays disabled for exactly as long as it does today.
 */
export function describeAutofillConfirm(input: {
  state: AutofillFlowState;
  hasSelection: boolean;
  isRunningExternally?: boolean;
}): AutofillConfirmDescriptor {
  const { state, hasSelection, isRunningExternally = false } = input;
  const busy = isAutofillFlowBusy(state) || isRunningExternally;
  const label = state.phase === 'saving' ? 'Saving source...' : busy ? 'Running autofill...' : 'Run autofill';
  return { label, busy, disabled: busy || !hasSelection };
}
